<template>
  <div class="wt-tags-list">
    <div class="text-instead-of-tags" v-if="showText">
      <span>{{ showTextAll ? mergedOptions.all : mergedOptions.none }}</span>
    </div>
    <div
      class="tags"
      v-else
      :class="{
        'tags-visibility-hidden': isRenderTagsProcess,
      }"
      @mousedown.stop
    >
      <wt-tags-list-item
        v-for="tag in selectedTags"
        class="tags-item"
        :key="tag.id"
        :tag="tag"
        :should-hide-tag="!isRenderTagsProcess && tagsMap[tag.id]"
        :on-remove="onTagRemove"
        ref="tags"
      >
      </wt-tags-list-item>
      <div
        class="tags-more"
        :class="{
          '-dropdown-active': showDropdown,
          '-has-dropdown': hasDropdown,
        }"
        ref="more"
        v-if="isRenderTagsProcess || numberTagsHidden"
        @click="toggleDropdown"
      >
        <span>{{ numberTagsHidden }} More...</span>
        <div
          class="tags-more-dropdown"
          v-if="hasDropdown"
          v-show="showDropdown"
        >
          <ul>
            <li v-for="(tag, i) in hiddenTags" :key="i">
              <wt-tags-list-item
                class="tags-more-item"
                :key="tag.id"
                :tag="tag"
                :show-title="true"
                :on-remove="onTagRemove"
              >
              </wt-tags-list-item>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * wt-tags-list Vue component
 *  props:
 *        tags - full list of tags (selected and un selected)
 *               it must be an array of objects.
 *               each object must have an id and label
 *
 *        selectedTagsIds - an array of selected tags ids ie: ['1', '2']
 *
 *        options -
 *            linesNumber: how many lines to display
 *            all: if exist and all tags are selected, show's it instead of the tags ie: all: 'all tags'
 *            none: if exist and no tags are selected, show's it instead of the tags ie: none: 'no tags'
 *
 *  events:
 *        removeTag - emit's an event of click on remove tag
 */
import wtTagsListItem from './wtTagsListItem.component.vue';

export default {
  components: {
    'wt-tags-list-item': wtTagsListItem,
  },
  props: {
    tags: {
      type: Array,
      default: () => [],
    },

    hasDropdown: {
      type: Boolean,
      default: true,
    },

    selectedTagsIds: {
      type: Array,
      default: () => [],
    },

    options: {
      type: Object,
      default: () => this.defaultOptions,
    },
  },

  data: function () {
    return {
      isRenderTagsProcess: true,
      isAnimationProcess: false,
      tagsMap: {},
      showDropdown: false,
      defaultOptions: {
        linesNumber: 1,
        all: null,
        none: null,
      },
    };
  },

  computed: {
    selectedTags: function () {
      return this.tags.filter((tag) =>
        this.selectedTagsIds.includes(tag && tag.id)
      );
    },

    numberTagsHidden: function () {
      return Object.keys(this.tagsMap).filter((tagId) => this.tagsMap[tagId])
        .length;
    },

    hiddenTags: function () {
      return this.selectedTags.filter(
        (tag) => tag && tag.id && this.tagsMap[tag.id]
      );
    },

    showTextAll: function () {
      return (
        this.mergedOptions.all &&
        this.tags.length &&
        this.selectedTags.length === this.tags.length
      );
    },

    showTextNone: function () {
      return this.mergedOptions.none && !this.selectedTags.length;
    },

    showText: function () {
      return this.showTextAll || this.showTextNone;
    },

    mergedOptions: function () {
      return Object.assign({}, this.defaultOptions, this.options);
    },
  },

  created: function () {
    window.addEventListener('resize', this.onResizeWindow);
    window.addEventListener('mousedown', this.detectClickOutside);
  },

  beforeDestroy: function () {
    window.removeEventListener('resize', this.onResizeWindow);
    window.removeEventListener('mousedown', this.detectClickOutside);
  },

  mounted: function () {
    // on mounted setTags (isRenderTagsProcess is default true)
    this.setTagsNextTick();
  },

  watch: {
    selectedTags: function () {
      this.setTagsNextTick();
    },

    numberTagsHidden: function (numberTags) {
      if (numberTags === 0) {
        this.showDropdown = false;
      }
    },
  },

  methods: {
    convertPxStringToNumber: function (str) {
      return +str.replace(/px/i, '');
    },

    getMarginRightAndLeft: function (el) {
      const elComputedStyle = getComputedStyle(el);
      const marginRight =
        this.convertPxStringToNumber(elComputedStyle.marginRight) || 0;
      const marginLeft =
        this.convertPxStringToNumber(elComputedStyle.marginLeft) || 0;
      return marginRight + marginLeft;
    },

    getClientRec(el) {
      return el.getBoundingClientRect();
    },

    setTags: function () {
      // if show text for all or none, no need to calc tags
      if (this.showText) {
        return;
      }

      this.tagsMap = {};

      const listWidth = this.getClientRec(this.$el).width;
      const tags = this.$refs.tags;
      const tagsMore = this.$refs.more;
      const tagsMoreWidth = tagsMore ? this.getClientRec(tagsMore).width : 0;
      let tagsWidthAcc = 0;

      const listWidthMultiplyLines = listWidth * this.mergedOptions.linesNumber;
      const tagsMoreTotalWidth =
        tagsMoreWidth + this.getMarginRightAndLeft(tagsMore);
      const totalWidthSpaceForTags =
        listWidthMultiplyLines - tagsMoreTotalWidth;

      tags.forEach((tagRef) => {
        const el = tagRef.$el;
        tagsWidthAcc +=
          this.getClientRec(el).width + this.getMarginRightAndLeft(el);
        this.tagsMap[tagRef.tag.id] = tagsWidthAcc > totalWidthSpaceForTags;
      });

      this.isRenderTagsProcess = false;
    },

    setTagsNextTick: function () {
      this.isRenderTagsProcess = true;

      // wait to next tick, to allowed the isRenderTagsProcess true to take affect
      this.$nextTick(() => {
        this.setTags();
      });
    },

    onTagRemove: function (e, id) {
      this.$emit('removeTag', id);
    },

    onResizeWindow: function (e) {
      e.preventDefault();
      this.setTagsNextTick();
    },

    detectClickOutside: function () {
      this.showDropdown = false;
    },

    toggleDropdown() {
      if (!this.hasDropdown) {
        return;
      }
      this.showDropdown = !this.showDropdown;
    },
  },
};
</script>
